/**
 * V3Shell — canonical shell layout component
 *
 * Built from docs/v3_canonical.html exclusively. Every major DOM block
 * cites the canonical line range it was derived from.
 *
 * canonical structure reference:
 *   Top strip (#strip):        canonical lines 562–575
 *   Shell wrapper (.shell):    canonical line  581
 *   Sidebar (.sb):             canonical lines 582–596
 *   Main content slot:         canonical lines 597–600 (.main > .content)
 *   (Soren Home is App-root via AriaFloatGate — no right rail)
 */

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchProjects } from '../../api/dashboard'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import GlobalSearch, { GlobalSearchTrigger } from './GlobalSearch'
import { useNavigate, useLocation } from 'react-router-dom'
import { TOKEN_KEY } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { switchPortal } from '../../lib/switchPortal'
import { useCashReportFlag } from '../../hooks/useCashReportFlag'
import { BrandWordmark } from '../brand/BrandWordmark'
import { fetchBillingStatus } from '../../api/billing'
import SubscriptionGraceBanner from '../billing/SubscriptionGraceBanner'
import SubscriptionPaywall from '../billing/SubscriptionPaywall'
import '../billing/subscriptionBilling.css'

interface V3ShellProps {
  children: ReactNode
}

export default function V3Shell({ children }: V3ShellProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { user, refetch } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [mobDrawerOpen, setMobDrawerOpen] = useState(false)
  const [portalBusy, setPortalBusy] = useState(false)
  const showVendorPortal = user?.has_vendor_portal === true
  const { enabled: cashReportOn } = useCashReportFlag()
  const { data: sidebarProjects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
    staleTime: 60_000,
  })
  const { data: billing } = useQuery({
    queryKey: ['billing-status'],
    queryFn: fetchBillingStatus,
    enabled: !!user && user.user_role !== 'vendor',
    staleTime: 60_000,
    retry: false,
  })
  const activeProjectCount = useMemo(
    () => sidebarProjects.filter((p) => p.status === 'active').length,
    [sidebarProjects],
  )

  async function goVendorPortal() {
    if (portalBusy) return
    setPortalBusy(true)
    try {
      await switchPortal('vendor', { refetch, navigate })
    } catch {
      /* keep menu usable */
    } finally {
      setPortalBusy(false)
    }
  }

  const openSearch = useCallback(() => setSearchOpen(true), [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const displayName = user?.name?.trim() || 'Account'
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'U'

  // ── helpers ────────────────────────────────────────────────────────────
  const isTab = (paths: string[]) => paths.some(p =>
    p === '/' ? pathname === p : pathname.startsWith(p)
  )
  const isPulsePage = /\/projects\/\d+\/pulse(?:\/|$|\?)/.test(pathname)
  const isProjectHubTab = !isPulsePage && (pathname === '/projects' ||
    (pathname.startsWith('/projects/') && !pathname.startsWith('/projects/new')))
  const isSidebarItem = (path: string) =>
    path === '/dashboard' ? pathname === path : pathname.startsWith(path)

  function handleLogout() {
    localStorage.removeItem(TOKEN_KEY)
    window.dispatchEvent(new CustomEvent('auth:logout'))
    navigate('/login', { replace: true })
  }

  function navTo(path: string) {
    setMobDrawerOpen(false)
    navigate(path)
  }

  const mobTabActive = (paths: string[]) => paths.some(p =>
    p === '/dashboard' ? pathname === p : pathname.startsWith(p),
  )

  return (
    <div style={{ paddingTop: '42px' }}>

      {/*
       * ─── TOP STRIP ──────────────────────────────────────────────────
       * canonical: lines 562–575
       * #strip — fixed, height 42px, z-index 300
       * Contains: brand mark + name | 5 tabs | search + who avatar
       */}
      <header id="strip">

        {/* canonical: line 563 — .brand (logo mark + name) */}
        <div
          className="brand"
          onClick={() => navigate('/dashboard')}
          style={{ cursor: 'pointer' }}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/dashboard') }}
        >
          <BrandWordmark />
        </div>

        {/* canonical: lines 564–571 — .tabs (5 tab buttons) */}
        <div className="tabs">
          {/* canonical: line 565 — tab 0: Dashboard */}
          <button
            className={`tab${isTab(['/dashboard']) ? ' on' : ''}`}
            onClick={() => navigate('/dashboard')}
          >
            <span className="tab-n">1</span>Dashboard
          </button>

          {/* canonical: line 566 — tab 1: New Project */}
          <button
            className={`tab${isTab(['/projects/new']) ? ' on' : ''}`}
            onClick={() => navigate('/projects/new')}
          >
            <span className="tab-n">2</span>New Project
          </button>

          {/* canonical: line 567 — tab 2: Project Hub */}
          <button
            className={`tab${isProjectHubTab ? ' on' : ''}`}
            data-testid="nav-project-hub"
            onClick={() => navigate('/projects')}
          >
            <span className="tab-n">3</span>Project Hub
          </button>

          {cashReportOn ? (
            <button
              className={`tab${isTab(['/cash-report']) ? ' on' : ''}`}
              data-testid="nav-cash-report"
              onClick={() => navigate('/cash-report')}
            >
              <span className="tab-n">4</span>Cash Report
            </button>
          ) : null}

          {/*
           * canonical: line 568 — tab 4: Settings
           * Note: canonical skips id="t3" (no tab 3 in strip) — gap in numbering is intentional.
           * Canonical inline styles: margin-left 14px, left border separator, ink4 color default
           */}
          <button
            className={`tab${isTab(['/settings']) ? ' on' : ''}`}
            style={{ marginLeft: '14px', paddingLeft: '14px', borderLeft: '1px solid var(--line)', color: isTab(['/settings']) ? 'var(--ink)' : 'var(--ink4)' }}
            onClick={() => navigate('/settings')}
          >
            ⚙ Settings
          </button>

          {/* canonical: line 569 — tab 5: Soren setup (ink4 default color) */}
          <button
            className={`tab${isTab(['/settings/aria']) ? ' on' : ''}`}
            style={{ color: isTab(['/settings/aria']) ? 'var(--ink)' : 'var(--ink4)' }}
            onClick={() => navigate('/settings/aria')}
          >
            S Soren setup
          </button>
        </div>

        {/* canonical: lines 572–574 — .strip-r (search + who avatar) */}
        <div className="strip-r">
          <GlobalSearchTrigger onOpen={openSearch} />
          <DropdownMenu.Root open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="who"
                data-testid="user-menu-trigger"
                aria-label="Account menu"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', font: 'inherit' }}
              >
                <div className="who-av">{initials}</div>
                {displayName.split(' ')[0]}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="user-menu-content"
                sideOffset={6}
                align="end"
                style={{
                  minWidth: 160,
                  background: '#fff',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  padding: '6px 0',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  zIndex: 400,
                }}
              >
                <DropdownMenu.Item
                  className="user-menu-item"
                  data-testid="user-menu-settings"
                  onSelect={() => navigate('/settings')}
                  style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', outline: 'none' }}
                >
                  Settings
                </DropdownMenu.Item>
                {showVendorPortal && (
                  <DropdownMenu.Item
                    className="user-menu-item"
                    data-testid="user-menu-vendor-portal"
                    disabled={portalBusy}
                    onSelect={() => { void goVendorPortal() }}
                    style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', outline: 'none' }}
                  >
                    {portalBusy ? 'Opening…' : 'Vendor portal'}
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item
                  className="user-menu-item"
                  data-testid="user-menu-logout"
                  onSelect={handleLogout}
                  style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', outline: 'none' }}
                >
                  Log out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>

      </header>
      {/* END TOP STRIP */}

      {/*
       * ─── SHELL WRAPPER ──────────────────────────────────────────────
       * canonical: line 581 — .shell (flex row: sidebar + main + aria-rail)
       */}
      <div className="shell">

        {/*
         * ─── SIDEBAR (.sb) ────────────────────────────────────────────
         * canonical: lines 582–596
         * Sticky at top:42px, height calc(100vh - 42px)
         * Workspace nav items + Account section + workspace switcher footer
         */}
        <nav className="sb" aria-label="Workspace">

          {/* canonical: line 583 — .sb-sec "Workspace" */}
          <div className="sb-sec">Workspace</div>

          {/* canonical: line 584 — Dashboard nav item */}
          <div
            className={`ni${isSidebarItem('/dashboard') ? ' on' : ''}`}
            onClick={() => navigate('/dashboard')}
          >
            <span className="ni-ic">▦</span>Dashboard
          </div>

          {/* canonical: line 585 — Projects nav item with count badge */}
          <div
            className={`ni${isSidebarItem('/projects') && !pathname.startsWith('/projects/new') ? ' on' : ''}`}
            data-testid="sidebar-projects"
            onClick={() => navigate('/projects')}
          >
            <span className="ni-ic">▢</span>Projects
            <span className="ni-ct" data-testid="sidebar-project-count">{activeProjectCount}</span>
          </div>

          {/* canonical: line 586 — Subs & Vendors nav item */}
          <div
            className={`ni${isSidebarItem('/subs') ? ' on' : ''}`}
            onClick={() => navigate('/subs')}
          >
            <span className="ni-ic">◐</span>Subs &amp; Vendors
          </div>

          {/* PR14 — Reports nav item (under Subs & Vendors) */}
          <div
            className={`ni${isSidebarItem('/reports') ? ' on' : ''}`}
            onClick={() => navigate('/reports')}
            data-testid="sidebar-reports"
          >
            <span className="ni-ic">▤</span>Reports
          </div>

          {cashReportOn ? (
            <div
              className={`ni${isSidebarItem('/cash-report') ? ' on' : ''}`}
              data-testid="sidebar-cash-report"
              onClick={() => navigate('/cash-report')}
            >
              <span className="ni-ic">$</span>Cash Report
            </div>
          ) : null}

          {/* canonical: line 587 — .sb-sec "Account" */}
          <div className="sb-sec">Account</div>

          {/* canonical: line 588 — Settings nav item */}
          <div
            className={`ni${isSidebarItem('/settings') ? ' on' : ''}`}
            onClick={() => navigate('/settings')}
          >
            <span className="ni-ic">⚙</span>Settings
          </div>

          <div
            className="ni"
            onClick={handleLogout}
            data-testid="sidebar-logout"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLogout() }}
          >
            <span className="ni-ic">↪</span>Log out
          </div>

          {/*
           * canonical: lines 589–594 — .sb-ft workspace switcher
           * Sticky to bottom of sidebar. Shows current org with chevron.
           */}
          <div className="sb-ft">
            <div className="workspace" data-testid="sidebar-workspace-name">
              <div style={{ width: '18px', height: '18px', borderRadius: '4px', background: 'var(--ink)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700 }}>
                {(billing?.company_name || user?.company_name || 'W').trim().charAt(0).toUpperCase() || 'W'}
              </div>
              {billing?.company_name || user?.company_name || 'Workspace'}
              <span style={{ marginLeft: 'auto' }}>⌄</span>
            </div>
          </div>

        </nav>
        {/* END SIDEBAR */}

        {/*
         * ─── MAIN CONTENT AREA ────────────────────────────────────────
         * canonical: lines 597–600 — .main > .content
         * Children prop renders inside .content (individual screens provide their own .topbar + .pc)
         */}
        <main className="main">
          <div className="content">
            <SubscriptionGraceBanner billing={billing ?? null} />
            <div className={billing?.blocked ? 'subscription-readonly-shell' : undefined}>
              {children}
            </div>
            <SubscriptionPaywall billing={billing ?? null} />
          </div>
        </main>
        {/* END MAIN */}

      </div>
      {/* END SHELL */}

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* canonical: lines 2405–2411 — mobile bottom nav + drawer for full workspace nav */}
      <div className="mob-tab" aria-label="Mobile navigation">
        <button
          type="button"
          className={`mt${mobTabActive(['/dashboard']) ? ' on' : ''}`}
          onClick={() => navTo('/dashboard')}
          data-testid="mob-nav-dashboard"
        >
          <div className="mt-ic">▦</div>
          <div className="mt-l">Home</div>
        </button>
        <button
          type="button"
          className={`mt${mobTabActive(['/projects/new']) ? ' on' : ''}`}
          onClick={() => navTo('/projects/new')}
          data-testid="mob-nav-new"
        >
          <div className="mt-ic">＋</div>
          <div className="mt-l">New</div>
        </button>
        <button
          type="button"
          className={`mt${mobTabActive(['/projects']) && !pathname.startsWith('/projects/new') ? ' on' : ''}`}
          onClick={() => navTo('/projects')}
          data-testid="mob-nav-projects"
        >
          <div className="mt-ic">▢</div>
          <div className="mt-l">Projects</div>
        </button>
        <button
          type="button"
          className={`mt${mobTabActive(['/reports']) ? ' on' : ''}`}
          onClick={() => navTo('/reports')}
          data-testid="mob-nav-reports"
        >
          <div className="mt-ic">▤</div>
          <div className="mt-l">Reports</div>
        </button>
        <button
          type="button"
          className={`mt${mobDrawerOpen ? ' on' : ''}`}
          onClick={() => setMobDrawerOpen((o) => !o)}
          data-testid="mob-nav-more"
        >
          <div className="mt-ic">☰</div>
          <div className="mt-l">More</div>
        </button>
      </div>

      {mobDrawerOpen && (
        <>
          <div
            role="presentation"
            onClick={() => setMobDrawerOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.35)',
              zIndex: 250,
            }}
          />
          <nav
            className="mob-drawer"
            aria-label="Mobile workspace menu"
            data-testid="mob-drawer"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 56,
              maxHeight: '60vh',
              overflowY: 'auto',
              background: '#fff',
              borderTop: '1px solid var(--line)',
              zIndex: 260,
              padding: '12px 0 calc(12px + env(safe-area-inset-bottom))',
              boxShadow: '0 -8px 24px rgba(0,0,0,0.08)',
            }}
          >
            <div className="sb-sec" style={{ padding: '0 16px' }}>Workspace</div>
            {[
              { path: '/dashboard', label: 'Dashboard', icon: '▦' },
              { path: '/projects', label: 'Projects', icon: '▢', active: pathname.startsWith('/projects') && !pathname.startsWith('/projects/new') },
              { path: '/subs', label: 'Subs & Vendors', icon: '◐' },
              { path: '/reports', label: 'Reports', icon: '▤', active: pathname.startsWith('/reports') },
              ...(cashReportOn ? [{ path: '/cash-report', label: 'Cash Report', icon: '$', active: pathname.startsWith('/cash-report') }] : []),
              { path: '/settings', label: 'Settings', icon: '⚙' },
            ].map((item) => (
              <div
                key={item.path}
                className={`ni${(item.active ?? isSidebarItem(item.path)) ? ' on' : ''}`}
                onClick={() => navTo(item.path)}
                style={{ margin: '0 8px' }}
              >
                <span className="ni-ic">{item.icon}</span>{item.label}
              </div>
            ))}
            <div
              className="ni"
              onClick={handleLogout}
              style={{ margin: '0 8px' }}
              data-testid="mob-drawer-logout"
            >
              <span className="ni-ic">↪</span>Log out
            </div>
          </nav>
        </>
      )}

    </div>
  )
}
